/**
 * WC-07: Challenge Store
 *
 * Manages state for the Weekly Challenge system using Zustand.
 * Syncs with the backend API at /api/challenges/*.
 * Updated: Auth retry logic added
 */

import { create } from 'zustand';
import type { ChallengeVisualAssets } from '../components/ChallengeBattlefield/ChallengeBattlefieldTypes';
import { getApiUrl } from '../config/api';
import { getAuthTokenWithRetry as getAuthToken } from '../services/auth-token';

// =====================================================================
// API UTILITIES
// =====================================================================

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  requiresAuth: boolean = true
): Promise<T> {
  const url = `${getApiUrl()}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>
  };

  if (requiresAuth) {
    const token = await getAuthToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'API request failed');
  }

  return data;
}

// =====================================================================
// TYPES
// =====================================================================

export type ChallengeMode =
  | 'standard_battle'
  | 'wave_survival'
  | 'gauntlet_run'
  | 'boss_fight'
  | 'defense';

export type DayStatus = 'locked' | 'available' | 'in_progress' | 'completed' | 'failed';

export interface DayResult {
  victory: boolean;
  stars: 0 | 1 | 2 | 3;
  unitsLost: number;
  turnsUsed: number;
  damageDealt: number;
  damageTaken: number;
  completedAt: string;
}

export interface DayProgress {
  day: number;
  status: DayStatus;
  attempts: number;
  bestResult?: DayResult;
  firstCompletedAt?: string;
  lastAttemptAt?: string;
}

export interface DayAvailability {
  day: number;
  available: boolean;
  reason?: 'locked' | 'future' | 'week_ended' | 'no_unlock';
  unlocksAt?: string;
}

export interface DayEncounter {
  day: number;
  name: string;
  mode: ChallengeMode;
  flavor: string;
  enemies: {
    name: string;
    difficulty: string;
    count: number;
  }[];
  mapTemplate: string;
  bossMechanics?: string[];
}

export interface WeekChallenge {
  weekId: string;
  theme: string;
  narrative: string;
  startDate?: string;
  endDate?: string;
  /** Encounter details for each day */
  encounters?: DayEncounter[];
  /** Alias for encounters - maintains backward compatibility with existing UI */
  days: DayEncounter[];
  status: 'active' | 'upcoming' | 'completed';
  difficultyCurve?: string;
  rewards?: RewardDefinition[];
}

export interface RewardDefinition {
  id: string;
  type: 'tokens' | 'creature_unlock' | 'badge' | 'upgrade_token';
  name: string;
  description: string;
  condition: RewardCondition;
  claimed: boolean;
}

export type RewardCondition =
  | { type: 'day_complete'; day: number }
  | { type: 'streak'; days: number }
  | { type: 'boss_defeat'; day: number }
  | { type: 'perfect_week' }
  | { type: 'stars'; count: number };

export interface UserChallengeProgress {
  weekId: string;
  days: Record<number, DayProgress>;
  currentStreak: number;
  maxStreak: number;
  totalAttempts: number;
  totalStars: number;
}

export interface UserChallengeStats {
  weeksParticipated: number;
  weeksCompleted: number;
  perfectWeeks: number;
  totalStarsEarned: number;
  longestStreak: number;
  currentWeekStreak: number;
  lastParticipatedWeek: string;
}

export interface ArchivedWeekSummary {
  weekId: string;
  challengeId: string;
  daysCompleted: number;
  totalDays: number;
  totalStars: number;
  maxPossibleStars: number;
  maxStreak: number;
  totalAttempts: number;
  perfectWeek: boolean;
  archivedAt: string;
}

export interface RewardBalance {
  challengeTokens: number;
  upgradeTokens: number;
}

export interface UnlockedCreature {
  creatureId: string;
  name: string;
  unlockedAt: string;
  source: string;
}

export interface EarnedBadge {
  badgeId: string;
  name: string;
  description: string;
  earnedAt: string;
}

export interface AttemptResult {
  victory: boolean;
  unitsLost: number;
  turnsUsed: number;
  damageDealt: number;
  damageTaken: number;
}

export interface StartAttemptResponse {
  attemptId: string;
  day: number;
  attemptNumber: number;
  startedAt: string;
}

// =====================================================================
// API RESPONSE TYPES
// =====================================================================

interface CurrentChallengeResponse {
  success: boolean;
  challenge: WeekChallenge;
}

interface ProgressResponse {
  success: boolean;
  weekId: string;
  progress: {
    days: Record<number, DayProgress>;
    currentStreak: number;
    maxStreak: number;
    totalAttempts: number;
    totalStars: number;
  };
  availability: DayAvailability[];
}

interface ChallengeDetailsResponse {
  success: boolean;
  challenge: WeekChallenge;
}

interface EncounterResponse {
  success: boolean;
  encounter: DayEncounter;
}

interface StatsResponse {
  success: boolean;
  stats: UserChallengeStats;
}

interface HistoryResponse {
  success: boolean;
  history: ArchivedWeekSummary[];
}

interface BalanceResponse {
  success: boolean;
  balance: RewardBalance;
}

interface CreaturesResponse {
  success: boolean;
  creatures: UnlockedCreature[];
}

interface BadgesResponse {
  success: boolean;
  badges: EarnedBadge[];
}

interface StartAttemptApiResponse {
  success: boolean;
  attemptId: string;
  day: number;
  attemptNumber: number;
  startedAt: string;
}

interface CompleteAttemptResponse {
  success: boolean;
  dayProgress: DayProgress;
  stars: number;
}

interface ClaimRewardResponse {
  success: boolean;
  grants: Array<{
    type: string;
    amount?: number;
    itemId?: string;
  }>;
}

// =====================================================================
// STORE INTERFACE
// =====================================================================

interface ChallengeStore {
  // Current week data
  currentWeek: WeekChallenge | null;
  progress: UserChallengeProgress | null;
  availability: DayAvailability[];

  // Selected encounter details
  selectedEncounter: DayEncounter | null;

  // Visual assets cache (week:day -> assets)
  visualAssetsCache: Map<string, ChallengeVisualAssets>;

  // User stats and history
  stats: UserChallengeStats | null;
  history: ArchivedWeekSummary[];

  // Rewards
  rewardBalance: RewardBalance | null;
  unlockedCreatures: UnlockedCreature[];
  earnedBadges: EarnedBadge[];

  // Current attempt
  currentAttemptId: string | null;

  // UI state
  selectedDay: number | null;
  selectedCreatures: string[];

  // Loading states
  isLoading: boolean;
  isLoadingProgress: boolean;
  isLoadingEncounter: boolean;
  isLoadingStats: boolean;
  isLoadingRewards: boolean;
  isStartingChallenge: boolean;
  isCompletingAttempt: boolean;

  // Error state
  error: string | null;

  // Challenge data actions
  fetchCurrentWeek: () => Promise<void>;
  fetchChallengeDetails: (weekId: string) => Promise<void>;
  fetchProgress: () => Promise<void>;
  fetchWeekProgress: (weekId: string) => Promise<void>;
  fetchEncounter: (weekId: string, day: number) => Promise<void>;

  // Stats and history actions
  fetchStats: () => Promise<void>;
  fetchHistory: () => Promise<void>;

  // Rewards actions
  fetchRewardBalance: () => Promise<void>;
  fetchUnlockedCreatures: () => Promise<void>;
  fetchEarnedBadges: () => Promise<void>;
  claimRewards: (weekId: string) => Promise<void>;

  // Gameplay actions
  startAttempt: (weekId: string, day: number) => Promise<StartAttemptResponse>;
  completeAttempt: (attemptId: string, result: AttemptResult) => Promise<void>;
  /** Convenience method for starting a challenge (legacy API compatibility) */
  startChallenge: (day: number, creatures: string[]) => Promise<string>;

  // Visual assets actions
  fetchVisualAssets: (weekId: string, day: number) => Promise<ChallengeVisualAssets | null>;
  getVisualAssets: (weekId: string, day: number) => ChallengeVisualAssets | null;
  clearVisualAssetsCache: () => void;

  // UI actions
  selectDay: (day: number | null) => void;
  setSelectedCreatures: (creatures: string[]) => void;
  toggleCreature: (creatureId: string) => void;

  // Utility actions
  clearError: () => void;
  reset: () => void;
}

// =====================================================================
// INITIAL STATE
// =====================================================================

const initialState = {
  // Data
  currentWeek: null,
  progress: null,
  availability: [],
  selectedEncounter: null,
  visualAssetsCache: new Map<string, ChallengeVisualAssets>(),
  stats: null,
  history: [],
  rewardBalance: null,
  unlockedCreatures: [],
  earnedBadges: [],
  currentAttemptId: null,

  // UI
  selectedDay: null,
  selectedCreatures: [],

  // Loading
  isLoading: false,
  isLoadingProgress: false,
  isLoadingEncounter: false,
  isLoadingStats: false,
  isLoadingRewards: false,
  isStartingChallenge: false,
  isCompletingAttempt: false,

  // Error
  error: null,
};

// =====================================================================
// STORE IMPLEMENTATION
// =====================================================================

/**
 * Normalize challenge data from API response.
 * Ensures `days` property is always populated from `encounters`.
 */
function normalizeChallenge(challenge: any): WeekChallenge {
  // The API may return encounters as an array or a count
  const encounters = Array.isArray(challenge.encounters)
    ? challenge.encounters
    : [];

  return {
    weekId: challenge.weekId,
    theme: challenge.theme,
    narrative: challenge.narrative,
    startDate: challenge.startDate,
    endDate: challenge.endDate,
    encounters: encounters,
    days: encounters, // Alias for backward compatibility
    status: challenge.status,
    difficultyCurve: challenge.difficultyCurve,
    rewards: challenge.rewards
  };
}

export const useChallengeStore = create<ChallengeStore>((set, get) => ({
  ...initialState,

  // ===================================================================
  // CHALLENGE DATA ACTIONS
  // ===================================================================

  /**
   * Fetch the currently active weekly challenge
   * GET /api/challenges/current (no auth required)
   */
  fetchCurrentWeek: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiRequest<CurrentChallengeResponse>(
        '/api/challenges/current',
        { method: 'GET' },
        false // No auth required for viewing current challenge
      );
      set({
        currentWeek: normalizeChallenge(response.challenge),
        isLoading: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch current challenge';
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Fetch detailed challenge data by week ID
   * GET /api/challenges/:weekId (no auth required)
   */
  fetchChallengeDetails: async (weekId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiRequest<ChallengeDetailsResponse>(
        `/api/challenges/${weekId}`,
        { method: 'GET' },
        false
      );
      set({
        currentWeek: normalizeChallenge(response.challenge),
        isLoading: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch challenge details';
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Fetch user's progress for the current active challenge
   * GET /api/challenges/progress (auth required)
   */
  fetchProgress: async () => {
    set({ isLoadingProgress: true, error: null });
    try {
      const response = await apiRequest<ProgressResponse>(
        '/api/challenges/progress',
        { method: 'GET' },
        true
      );
      set({
        progress: {
          weekId: response.weekId,
          days: response.progress.days,
          currentStreak: response.progress.currentStreak,
          maxStreak: response.progress.maxStreak,
          totalAttempts: response.progress.totalAttempts,
          totalStars: response.progress.totalStars
        },
        availability: response.availability,
        isLoadingProgress: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch progress';
      set({ error: message, isLoadingProgress: false });
    }
  },

  /**
   * Fetch user's progress for a specific week
   * GET /api/challenges/:weekId/progress (auth required)
   */
  fetchWeekProgress: async (weekId: string) => {
    set({ isLoadingProgress: true, error: null });
    try {
      const response = await apiRequest<ProgressResponse>(
        `/api/challenges/${weekId}/progress`,
        { method: 'GET' },
        true
      );
      set({
        progress: {
          weekId: response.weekId,
          days: response.progress.days,
          currentStreak: response.progress.currentStreak,
          maxStreak: response.progress.maxStreak,
          totalAttempts: response.progress.totalAttempts,
          totalStars: response.progress.totalStars
        },
        availability: response.availability,
        isLoadingProgress: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch week progress';
      set({ error: message, isLoadingProgress: false });
    }
  },

  /**
   * Fetch encounter details for a specific day
   * GET /api/challenges/:weekId/encounters/:day (no auth required)
   */
  fetchEncounter: async (weekId: string, day: number) => {
    set({ isLoadingEncounter: true, error: null });
    try {
      const response = await apiRequest<EncounterResponse>(
        `/api/challenges/${weekId}/encounters/${day}`,
        { method: 'GET' },
        false
      );
      set({
        selectedEncounter: response.encounter,
        isLoadingEncounter: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch encounter';
      set({ error: message, isLoadingEncounter: false });
    }
  },

  // ===================================================================
  // STATS AND HISTORY ACTIONS
  // ===================================================================

  /**
   * Fetch user's overall challenge statistics
   * GET /api/challenges/stats (auth required)
   */
  fetchStats: async () => {
    set({ isLoadingStats: true, error: null });
    try {
      const response = await apiRequest<StatsResponse>(
        '/api/challenges/stats',
        { method: 'GET' },
        true
      );
      set({
        stats: response.stats,
        isLoadingStats: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch stats';
      set({ error: message, isLoadingStats: false });
    }
  },

  /**
   * Fetch user's archived challenge history
   * GET /api/challenges/history (auth required)
   */
  fetchHistory: async () => {
    set({ isLoadingStats: true, error: null });
    try {
      const response = await apiRequest<HistoryResponse>(
        '/api/challenges/history',
        { method: 'GET' },
        true
      );
      set({
        history: response.history,
        isLoadingStats: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch history';
      set({ error: message, isLoadingStats: false });
    }
  },

  // ===================================================================
  // REWARDS ACTIONS
  // ===================================================================

  /**
   * Fetch user's challenge currency balance
   * GET /api/challenges/rewards/balance (auth required)
   */
  fetchRewardBalance: async () => {
    set({ isLoadingRewards: true, error: null });
    try {
      const response = await apiRequest<BalanceResponse>(
        '/api/challenges/rewards/balance',
        { method: 'GET' },
        true
      );
      set({
        rewardBalance: response.balance,
        isLoadingRewards: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch reward balance';
      set({ error: message, isLoadingRewards: false });
    }
  },

  /**
   * Fetch user's unlocked challenge creatures
   * GET /api/challenges/rewards/creatures (auth required)
   */
  fetchUnlockedCreatures: async () => {
    set({ isLoadingRewards: true, error: null });
    try {
      const response = await apiRequest<CreaturesResponse>(
        '/api/challenges/rewards/creatures',
        { method: 'GET' },
        true
      );
      set({
        unlockedCreatures: response.creatures,
        isLoadingRewards: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch unlocked creatures';
      set({ error: message, isLoadingRewards: false });
    }
  },

  /**
   * Fetch user's earned badges
   * GET /api/challenges/rewards/badges (auth required)
   */
  fetchEarnedBadges: async () => {
    set({ isLoadingRewards: true, error: null });
    try {
      const response = await apiRequest<BadgesResponse>(
        '/api/challenges/rewards/badges',
        { method: 'GET' },
        true
      );
      set({
        earnedBadges: response.badges,
        isLoadingRewards: false
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch earned badges';
      set({ error: message, isLoadingRewards: false });
    }
  },

  /**
   * Claim pending rewards for a week
   * POST /api/challenges/rewards/claim (auth required)
   */
  claimRewards: async (weekId: string) => {
    set({ isLoadingRewards: true, error: null });
    try {
      await apiRequest<ClaimRewardResponse>(
        '/api/challenges/rewards/claim',
        {
          method: 'POST',
          body: JSON.stringify({ weekId })
        },
        true
      );

      // Refresh balance after claiming
      await get().fetchRewardBalance();

      set({ isLoadingRewards: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to claim rewards';
      set({ error: message, isLoadingRewards: false });
      throw error;
    }
  },

  // ===================================================================
  // GAMEPLAY ACTIONS
  // ===================================================================

  /**
   * Start a challenge attempt for a specific day
   * POST /api/challenges/:weekId/days/:day/start (auth required)
   */
  startAttempt: async (weekId: string, day: number): Promise<StartAttemptResponse> => {
    set({ isStartingChallenge: true, error: null });
    try {
      const response = await apiRequest<StartAttemptApiResponse>(
        `/api/challenges/${weekId}/days/${day}/start`,
        { method: 'POST' },
        true
      );

      set({
        currentAttemptId: response.attemptId,
        isStartingChallenge: false
      });

      return {
        attemptId: response.attemptId,
        day: response.day,
        attemptNumber: response.attemptNumber,
        startedAt: response.startedAt
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start challenge';
      set({ error: message, isStartingChallenge: false });
      throw error;
    }
  },

  /**
   * Complete a challenge attempt with results
   * POST /api/challenges/attempts/:attemptId/complete (auth required)
   */
  completeAttempt: async (attemptId: string, result: AttemptResult) => {
    set({ isCompletingAttempt: true, error: null });
    try {
      await apiRequest<CompleteAttemptResponse>(
        `/api/challenges/attempts/${attemptId}/complete`,
        {
          method: 'POST',
          body: JSON.stringify(result)
        },
        true
      );

      set({
        currentAttemptId: null,
        isCompletingAttempt: false
      });

      // Refresh progress after completing
      await get().fetchProgress();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to complete attempt';
      set({ error: message, isCompletingAttempt: false });
      throw error;
    }
  },

  /**
   * Convenience method for starting a challenge (legacy API compatibility)
   * Combines startAttempt with creature selection
   * Returns a matchId for navigation to battle page
   */
  startChallenge: async (day: number, creatures: string[]): Promise<string> => {
    const { currentWeek, setSelectedCreatures } = get();

    if (!currentWeek) {
      throw new Error('No active challenge');
    }

    // Store selected creatures
    setSelectedCreatures(creatures);

    // Start the attempt
    const attempt = await get().startAttempt(currentWeek.weekId, day);

    // Return the attemptId as the matchId for battle navigation
    return attempt.attemptId;
  },

  // ===================================================================
  // UI ACTIONS
  // ===================================================================

  /**
   * Select a day for viewing/playing
   */
  selectDay: (day: number | null) => {
    set({ selectedDay: day });
  },

  /**
   * Set the selected creatures for battle
   */
  setSelectedCreatures: (creatures: string[]) => {
    set({ selectedCreatures: creatures });
  },

  /**
   * Toggle a creature in/out of selection (max 6)
   */
  toggleCreature: (creatureId: string) => {
    const current = get().selectedCreatures;
    if (current.includes(creatureId)) {
      set({ selectedCreatures: current.filter(id => id !== creatureId) });
    } else if (current.length < 6) {
      set({ selectedCreatures: [...current, creatureId] });
    }
  },

  // ===================================================================
  // VISUAL ASSETS ACTIONS
  // ===================================================================

  /**
   * Fetch visual assets for a specific day
   * GET /api/challenges/:weekId/days/:day/visual-assets
   * Caches result in visualAssetsCache
   */
  fetchVisualAssets: async (weekId: string, day: number): Promise<ChallengeVisualAssets | null> => {
    const cacheKey = `${weekId}:${day}`;

    // Check cache first
    const cached = get().visualAssetsCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await apiRequest<{ success: boolean; assets: ChallengeVisualAssets }>(
        `/api/challenges/${weekId}/days/${day}/visual-assets`,
        { method: 'GET' },
        false // Public endpoint
      );

      if (response.success && response.assets) {
        // Update cache
        const newCache = new Map(get().visualAssetsCache);
        newCache.set(cacheKey, response.assets);
        set({ visualAssetsCache: newCache });

        return response.assets;
      }

      return null;
    } catch (error) {
      // 404 means assets not generated - return null without logging error
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      console.error('[ChallengeStore] Failed to fetch visual assets:', error);
      return null;
    }
  },

  /**
   * Get cached visual assets for a day (synchronous)
   * Returns null if not in cache
   */
  getVisualAssets: (weekId: string, day: number): ChallengeVisualAssets | null => {
    const cacheKey = `${weekId}:${day}`;
    return get().visualAssetsCache.get(cacheKey) || null;
  },

  /**
   * Clear visual assets cache
   */
  clearVisualAssetsCache: () => {
    set({ visualAssetsCache: new Map() });
  },

  // ===================================================================
  // UTILITY ACTIONS
  // ===================================================================

  /**
   * Clear any error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },
}));

// =====================================================================
// SELECTORS (Computed/Derived State)
// =====================================================================

/**
 * Get days completed count
 */
export function getDaysCompleted(progress: UserChallengeProgress | null): number {
  if (!progress) return 0;
  return Object.values(progress.days).filter(d => d.status === 'completed').length;
}

/**
 * Get total stars earned this week
 */
export function getTotalStars(progress: UserChallengeProgress | null): number {
  if (!progress) return 0;
  return Object.values(progress.days).reduce(
    (sum, d) => sum + (d.bestResult?.stars || 0),
    0
  );
}

/**
 * Check if day is available for play
 */
export function isDayAvailable(
  day: number,
  availability: DayAvailability[]
): boolean {
  const dayInfo = availability.find(a => a.day === day);
  return dayInfo?.available ?? false;
}

/**
 * Get progress for a specific day
 */
export function getDayProgress(
  day: number,
  progress: UserChallengeProgress | null
): DayProgress | null {
  if (!progress) return null;
  return progress.days[day] || null;
}

/**
 * Check if week is complete (all 7 days done)
 */
export function isWeekComplete(progress: UserChallengeProgress | null): boolean {
  return getDaysCompleted(progress) === 7;
}

/**
 * Check if week is perfect (all 21 stars)
 */
export function isPerfectWeek(progress: UserChallengeProgress | null): boolean {
  return getTotalStars(progress) === 21;
}

// =====================================================================
// HELPER FUNCTIONS
// =====================================================================

export function formatModeName(mode: ChallengeMode): string {
  switch (mode) {
    case 'standard_battle': return 'Battle';
    case 'wave_survival': return 'Waves';
    case 'gauntlet_run': return 'Gauntlet';
    case 'boss_fight': return 'Boss';
    case 'defense': return 'Defense';
    default: return mode;
  }
}

export function getModeIcon(mode: ChallengeMode): string {
  switch (mode) {
    case 'standard_battle': return 'swords';
    case 'wave_survival': return 'waves';
    case 'gauntlet_run': return 'run';
    case 'boss_fight': return 'crown';
    case 'defense': return 'shield';
    default: return 'swords';
  }
}

export function getStatusIcon(status: DayStatus): string {
  switch (status) {
    case 'locked': return 'lock';
    case 'available': return 'play';
    case 'in_progress': return 'play-circle';
    case 'completed': return 'check-circle';
    case 'failed': return 'x-circle';
    default: return 'help-circle';
  }
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function getStarDisplay(stars: number): string {
  const filled = Math.min(Math.max(0, stars), 3);
  const empty = 3 - filled;
  return '\u2605'.repeat(filled) + '\u2606'.repeat(empty);
}

export function getDayName(day: number): string {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return days[day - 1] || `Day ${day}`;
}

export function getShortDayName(day: number): string {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days[day - 1] || `D${day}`;
}
